import { CheckCircle2, Download, X, XCircle } from "lucide-react";

import { useExports, type TrackedExport } from "../lib/export-tray-store.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Progress } from "./ui/progress.js";

/**
 * The floating export tray, bottom-right, mounted in the SHELL — outside the keyed
 * screens — so a job started on one report is still visible from any other. Hidden
 * entirely while there is nothing to show; a permanently docked panel would spend screen
 * space announcing the absence of exports.
 *
 * The finished file downloads itself once; the Download button stays because browsers
 * sometimes block the automatic click, and because "download it again" is a real need.
 */
export function ExportTray({
  onNavigate,
}: {
  readonly onNavigate: (to: string) => void;
}): React.JSX.Element | null {
  const exports = useExports();
  if (exports === null || exports.jobs.length === 0) return null;

  const finished = exports.jobs.some((job) => job.status === "ready" || job.status === "failed");

  return (
    <aside
      aria-label="Exports in progress"
      className="bg-card fixed right-4 bottom-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border shadow-lg"
    >
      <header className="flex items-center gap-2 border-b px-3 py-2">
        <p className="text-sm font-medium">Exports</p>
        <div className="flex-1" />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
          onClick={() => {
            onNavigate("exports");
          }}
        >
          My Exports
        </button>
        {finished && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
            onClick={exports.clearFinished}
          >
            Clear finished
          </button>
        )}
      </header>

      <ul className="flex max-h-80 flex-col divide-y overflow-y-auto">
        {exports.jobs.map((job) => (
          <TrayRow
            key={job.id}
            job={job}
            onDownload={() => void exports.download(job.id).catch(() => undefined)}
            onDismiss={() => {
              exports.dismiss(job.id);
            }}
          />
        ))}
      </ul>
    </aside>
  );
}

function TrayRow({
  job,
  onDownload,
  onDismiss,
}: {
  readonly job: TrackedExport;
  readonly onDownload: () => void;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  return (
    <li className="flex flex-col gap-1.5 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-medium">{job.label}</p>
        <Badge variant="outline" className="uppercase">
          {job.format}
        </Badge>
        <button
          type="button"
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
          onClick={onDismiss}
        >
          <X className="size-3.5" />
        </button>
      </div>

      {(job.status === "queued" || job.status === "processing") && (
        <>
          <Progress value={job.status === "queued" ? 3 : job.progress} />
          <p className="text-muted-foreground text-xs">
            {job.status === "queued"
              ? "Starting…"
              : `Generating — ${String(job.rowCount ?? 0)} rows so far`}
          </p>
        </>
      )}

      {job.status === "ready" && (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-4 text-emerald-600" />
          <p className="text-muted-foreground flex-1 text-xs">
            {String(job.rowCount ?? 0)} rows{job.downloaded ? " — downloaded" : ""}
          </p>
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="size-3.5" />
            Download
          </Button>
        </div>
      )}
      {job.status === "ready" && job.truncated && (
        // The ceiling is REPORTED here exactly as the worker reported it — a file that
        // stops at a round number with no note reads as "that's all the data there was".
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Row limit reached — the file holds the first {String(job.rowCount ?? 0)} rows.
        </p>
      )}

      {job.status === "failed" && (
        <div className="flex items-start gap-2">
          <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
          <p className="text-destructive text-xs">{job.error ?? "Export failed."}</p>
        </div>
      )}
    </li>
  );
}
